import {
  AdminCustomSelect,
  AdminSpinner,
  Button,
  Callout,
  Disclaimer,
  Div,
  P,
  SuccessToast,
  reportError,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { NoDataComponent } from '../../../adminComponents/NoDataComponent/NoDataComponent';
import { ProviderConnectionTypeComponent } from '../../../adminComponents/ProviderConnectionTypeComponent/ProviderConnectionTypeComponent';
import {
  getBrandMenuProviders,
  updateBrandMenuProviders,
} from '../../../api/menu.api';
import { ModalPortal } from '../../../components/modal/modalPortal';
import { renderProviderIcon, renderProviderName } from '../../../helpers';
import { BrandMenuResponse } from '../../../types/api/menu.types';
import {
  MenuProviderChoice,
  groupMenuProviders,
  menuAssignmentsFor,
} from '../helpers/brandMenus';
import { ProviderIconRow } from './MenuPicker.styled';

type Picked = Record<string, number | undefined>;

const initialPicks = (rows: MenuProviderChoice[]): Picked =>
  Object.fromEntries(rows.map((r) => [r.key, r.menuId]));

const rowLabel = (row: MenuProviderChoice) =>
  row.kitchenHubGroup
    ? `KitchenHub (${row.providers
        .map((p) => renderProviderName(p))
        .join(', ')})`
    : renderProviderName(row.providers[0]);

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  locationId: string;
  brandId: string;
  menus: BrandMenuResponse[];
  /** The menu list's `providers` change with every save; the caller refetches. */
  onSaved: () => void;
};

/**
 * Which menu each connected provider publishes. Only connected providers that
 * can publish a menu are listed; the KitchenHub providers of the brand appear
 * as one row because they always share a menu. Saving sends only the rows
 * that changed.
 */
export const MenuProvidersModal = ({
  isOpen,
  closeModal,
  locationId,
  brandId,
  menus,
  onSaved,
}: Props) => {
  const [rows, setRows] = useState<MenuProviderChoice[]>([]);
  const [picked, setPicked] = useState<Picked>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await getBrandMenuProviders(locationId, brandId);
        const grouped = groupMenuProviders(data.data);
        setRows(grouped);
        setPicked(initialPicks(grouped));
      } catch (error) {
        reportError(error);
        closeModal();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, locationId, brandId]);

  const menuOptions = menus.map((m) => ({
    name: m.isDefault ? `${m.name} (default)` : m.name,
    value: m.id,
  }));

  const assignments = menuAssignmentsFor(rows, picked);

  const handleSave = async () => {
    if (assignments.length === 0) return closeModal();
    try {
      setSaving(true);
      const { data } = await updateBrandMenuProviders(locationId, brandId, {
        assignments,
      });
      const grouped = groupMenuProviders(data.data);
      setRows(grouped);
      setPicked(initialPicks(grouped));
      SuccessToast(data.message);
      onSaved();
      closeModal();
    } catch (error) {
      reportError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading && <AdminSpinner />}
      <ModalPortal
        isOpen={isOpen}
        closeModal={closeModal}
        logo={false}
        header="Menu per Provider"
        width="640px"
        textAlign="left"
        overflow="visible"
      >
        <Div $mb="16">
          <Callout tone="info">
            Every connected provider publishes one menu. A provider without a
            menu of its own follows the default menu. Changing a provider's menu
            sends that menu's last published version to it right away.
          </Callout>
        </Div>

        {rows.map((row) => (
          <Div
            key={row.key}
            $box
            $p="12px 16px"
            $flex
            $alignCenter
            $between
            $gap="16px"
            $mb="8"
            $columnSM
            $alignStartSM
          >
            <Div $flex $alignCenter $gap="12px">
              <ProviderIconRow>
                {row.providers.map((p) => (
                  <img key={p} src={renderProviderIcon(p)} alt="" />
                ))}
              </ProviderIconRow>
              <Div>
                <Div $flex $alignCenter $gap="8px" $wrap>
                  <P $m="0">
                    <b>{rowLabel(row)}</b>
                  </P>
                  <ProviderConnectionTypeComponent
                    connectionType={row.connectionType}
                    shortText
                  />
                </Div>
                <Disclaimer $m="0">
                  {row.assigned
                    ? 'Has its own menu'
                    : 'Follows the default menu'}
                </Disclaimer>
              </Div>
            </Div>

            <Div $width="240px" $widthSM="100%" $flexNone>
              <AdminCustomSelect<number>
                value={picked[row.key] ?? null}
                onChange={(value) => {
                  if (typeof value === 'number')
                    setPicked((prev) => ({ ...prev, [row.key]: value }));
                }}
                options={menuOptions}
                placeholder="Select menu"
                $shadowDesign={false}
              />
            </Div>
          </Div>
        ))}

        {!loading && rows.length === 0 && (
          <NoDataComponent
            height="160px"
            heightSM="160px"
            title="No providers"
            description="No connected provider can publish a menu yet."
          />
        )}

        {rows.length > 0 && (
          <Button
            $selected
            $w="100%"
            $mt="24"
            $loading={saving}
            disabled={saving || assignments.length === 0}
            onClick={handleSave}
          >
            Save
          </Button>
        )}
      </ModalPortal>
    </>
  );
};
